<script setup lang="ts">
import { ref, watch, computed, nextTick } from 'vue'
import { useSongStore } from '../../stores/songStore'
import { useBandStore } from '../../stores/bandStore'
import type { SongPartDto, SongPartLogDto } from '../../dtos'
import { parsePerformance } from '../../utils/performance'

const props = defineProps<{
  part: SongPartDto
}>()

const emit = defineEmits(['back'])

const songStore = useSongStore()
const bandStore = useBandStore()

// Track the live part from the store so updates (e.g. rename) are reflected immediately
const part = computed(() => {
  const live = songStore.currentSong?.parts.find(p => p.id === props.part.id)
  return live ?? props.part
})
const performance = ref('')
const isSaving = ref(false)
const textareaEl = ref<HTMLTextAreaElement | null>(null)
const cursorPos = ref(0)
const showChordDialog = ref(false)
const chordInput = ref('')
const showRenameDialog = ref(false)
const renameInput = ref('')
const isRenaming = ref(false)
const logs = ref<SongPartLogDto[]>([])
const logsLoading = ref(false)
const logsLoaded = ref(false)
const showRevertDialog = ref(false)
const revertLog = ref<SongPartLogDto | null>(null)
const isReverting = ref(false)

const fetchLogs = async () => {
  if (logsLoaded.value) return
  logsLoading.value = true
  try {
    logs.value = await songStore.fetchSongPartLogs(part.value.id)
    logsLoaded.value = true
  } finally {
    logsLoading.value = false
  }
}

const formatLogDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
    date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

const formatLogMessage = (log: SongPartLogDto) => {
  if (log.action === 'Created') return log.newValue || 'Part created'
  if (log.field === 'Performance') return 'Updated lyrics & chords'
  if (log.field === 'IsActive') return log.newValue === 'True' ? 'Set to active' : 'Set to inactive'
  if (log.field) return `Changed ${log.field.toLowerCase()}`
  return log.action
}

const openRenameDialog = () => {
  renameInput.value = part.value.name
  showRenameDialog.value = true
}

const handleRename = async () => {
  if (!renameInput.value.trim()) return
  isRenaming.value = true
  try {
    await songStore.updateSongPart(part.value.id, { name: renameInput.value.trim() })
    showRenameDialog.value = false
  } catch (error) {
    console.error(error)
  } finally {
    isRenaming.value = false
  }
}

const saveCursorPos = () => {
  if (textareaEl.value) {
    cursorPos.value = textareaEl.value.selectionStart ?? 0
  }
}

const openChordDialog = () => {
  saveCursorPos()
  chordInput.value = ''
  showChordDialog.value = true
}

const insertChord = () => {
  if (!chordInput.value.trim()) return
  const chord = `[${chordInput.value.trim()}]`
  const pos = cursorPos.value
  performance.value = performance.value.slice(0, pos) + chord + performance.value.slice(pos)
  showChordDialog.value = false
  const newPos = pos + chord.length
  nextTick(() => {
    textareaEl.value?.focus()
    textareaEl.value?.setSelectionRange(newPos, newPos)
  })
}


const confirmRevert = (log: SongPartLogDto) => {
  revertLog.value = log
  showRevertDialog.value = true
}

const handleRevert = async () => {
  if (!revertLog.value?.previousValue || !revertLog.value.field) return
  isReverting.value = true
  try {
    const field = revertLog.value.field
    const value = revertLog.value.previousValue
    const data: Record<string, unknown> = {}

    if (field === 'Name') data.name = value
    else if (field === 'Performance') data.performance = value
    else if (field === 'IsActive') data.isActive = value === 'True'
    else if (field === 'SequenceNumber') data.sequenceNumber = parseInt(value)

    await songStore.updateSongPart(part.value.id, data)
    if (field === 'Performance') performance.value = value
    showRevertDialog.value = false
    // Refresh logs
    logsLoaded.value = false
    await fetchLogs()
  } catch (error) {
    console.error(error)
  } finally {
    isReverting.value = false
  }
}

// Watch for part ID changes to reset local state
watch(() => part.value.id, () => {
  performance.value = part.value.performance || ''
  logs.value = []
  logsLoaded.value = false
}, { immediate: true })

const parsedLines = computed(() => parsePerformance(performance.value))

const handleSave = async () => {
  isSaving.value = true
  try {
    await songStore.updateSongPart(part.value.id, { performance: performance.value })
  } catch (error) {
    console.error(error)
  } finally {
    isSaving.value = false
  }
}

const handleBack = async () => {
  // If content has changed, save it before going back
  if (performance.value !== (part.value.performance || '')) {
    await handleSave()
  }
  emit('back')
}

watch(() => part.value.performance, (newVal) => {
  performance.value = newVal || ''
}, { immediate: true })
</script>

<template>
  <div class="space-y-6 p-1 lg:p-8 bg-slate-200 min-h-full rounded-xl">
    <!-- Header -->
    <div class="flex items-center justify-between mb-2">
      <div class="flex items-center gap-4">
        <Button icon="pi pi-arrow-left" text rounded severity="secondary" @click="handleBack" />
        <div>
          <p class="rd-label leading-none mb-1">Song Part</p>
          <div class="flex items-center gap-2">
            <h1 class="text-2xl font-black text-slate-900 leading-tight">{{ part.name }}</h1>
            <Button v-if="bandStore.canEditSongs" icon="pi pi-pencil" text rounded severity="secondary" size="small" class="opacity-40 hover:opacity-100" v-tooltip.top="'Rename part'" @click="openRenameDialog" />
          </div>
        </div>
      </div>
      <Button v-if="bandStore.canEditSongs" label="Save" icon="pi pi-save" size="small" severity="success" :loading="isSaving" @click="handleSave" />
    </div>

    <!-- Performance Editor -->
    <div class="p-6 rd-card-inner space-y-4">
      <div class="space-y-2">
        <label class="rd-label">Performance (Lyrics & Chords)</label>
        <div v-if="bandStore.canEditSongs" class="flex gap-2 mb-2">
          <Button label="Chord" icon="pi pi-hashtag" size="small" severity="secondary" outlined class="text-xs !border-slate-400" @click="openChordDialog" />
        </div>
        <textarea
          ref="textareaEl"
          v-model="performance"
          rows="10"
          wrap="off"
          class="w-full font-mono text-sm p-4 rounded-2xl border border-slate-300 focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-200 transition-all bg-slate-50/50 resize-y overflow-x-auto whitespace-pre"
          placeholder="     [Em]              [G]&#10;Now she's walking through the clouds"
          :readonly="!bandStore.canEditSongs"
          @click="saveCursorPos"
          @keyup="saveCursorPos"
        ></textarea>
        <p class="text-[10px] text-slate-400 italic">Tip: Wrap chords in square brackets like <code class="px-1">[Em]</code>. On a line of only chords, they render in the columns you typed. Inline <code class="px-1">[Chord]</code> markers sit above the next lyric character.</p>
      </div>
    </div>

    <!-- Preview (Aligned Chords & Lyrics) -->
    <div v-if="performance" class="p-6 rd-card-inner overflow-x-auto">
      <p class="rd-label mb-6">Preview</p>

      <div class="font-mono">
        <div v-for="(line, index) in parsedLines" :key="index"
             :class="line.segments.length === 0 ? 'h-3' : 'leading-tight whitespace-pre text-[16px]'">
          <span v-for="(seg, sIdx) in line.segments" :key="sIdx"
                :class="seg.type === 'chord' ? 'text-teal-600 font-bold' : 'text-slate-700'">{{ seg.text }}</span>
        </div>
      </div>
    </div>
    <!-- Change Log -->
    <div class="rd-card-inner border-slate-300 overflow-hidden">
      <Accordion @tab-open="fetchLogs" class="song-parts-accordion">
        <AccordionPanel value="0">
          <AccordionHeader class="p-6">
            <span class="text-sm font-bold text-slate-600">Change Log</span>
          </AccordionHeader>
          <AccordionContent class="px-6 pb-6">
          <div v-if="logsLoading" class="py-4 text-center text-sm text-slate-400">Loading...</div>
          <div v-else-if="logs.length === 0" class="py-4 text-center text-sm text-slate-400">No changes recorded yet.</div>
          <div v-else class="divide-y divide-slate-100">
            <div v-for="log in logs" :key="log.id" class="py-3 flex items-start gap-3">
              <div class="flex-1 min-w-0">
                <p class="text-sm text-slate-700">
                  <span class="font-semibold">{{ log.displayName || 'Unknown' }}</span>
                  <span class="text-slate-500"> — {{ formatLogMessage(log) }}</span>
                </p>
                <p v-if="log.field && log.field !== 'Performance' && log.field !== 'IsActive' && log.previousValue" class="text-xs text-slate-400 mt-0.5">
                  <span class="line-through">{{ log.previousValue }}</span> → {{ log.newValue }}
                </p>
                <div v-if="log.field === 'Performance'" class="mt-1 text-xs space-y-1">
                  <pre v-if="log.previousValue" class="text-slate-400 line-through whitespace-pre-wrap bg-red-50 rounded p-2 max-h-32 overflow-y-auto">{{ log.previousValue }}</pre>
                  <pre v-if="log.newValue" class="text-slate-600 whitespace-pre-wrap bg-green-50 rounded p-2 max-h-32 overflow-y-auto">{{ log.newValue }}</pre>
                </div>
                <div class="flex items-center gap-2 mt-0.5">
                  <p class="text-[10px] text-slate-400">{{ formatLogDate(log.createdAt) }}</p>
                  <Button v-if="bandStore.canEditSongs && log.field && log.previousValue" icon="pi pi-undo" text rounded size="small"
                          class="!w-5 !h-5 !p-0 opacity-60 hover:opacity-100"
                          v-tooltip.top="'Revert to previous value'"
                          @click="confirmRevert(log)" />
                </div>
              </div>
            </div>
          </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </div>

    <!-- Revert Confirmation Dialog -->
    <Dialog v-model:visible="showRevertDialog" modal header="Revert Change" :style="{ width: '24rem' }">
      <div class="flex flex-col gap-4 py-2">
        <p class="text-sm text-slate-600">
          Revert <span class="font-semibold">{{ revertLog?.field?.toLowerCase() }}</span> to its previous value?
        </p>
        <div v-if="revertLog?.field === 'Performance'" class="text-xs">
          <pre class="whitespace-pre-wrap bg-slate-50 rounded p-2 max-h-32 overflow-y-auto text-slate-600">{{ revertLog?.previousValue }}</pre>
        </div>
        <p v-else-if="revertLog?.previousValue" class="text-sm font-mono bg-slate-50 rounded p-2 text-slate-700">{{ revertLog?.previousValue }}</p>
        <div class="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" text size="small" @click="showRevertDialog = false" />
          <Button label="Revert" severity="warn" size="small" :loading="isReverting" @click="handleRevert" />
        </div>
      </div>
    </Dialog>

    <!-- Rename Part Dialog -->
    <Dialog v-model:visible="showRenameDialog" modal header="Rename Part" :style="{ width: '20rem' }">
      <div class="flex flex-col gap-4 py-2">
        <InputText v-model="renameInput" class="w-full" autofocus @keyup.enter="handleRename" />
        <div class="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" text size="small" @click="showRenameDialog = false" />
          <Button label="Save" size="small" :loading="isRenaming" @click="handleRename" />
        </div>
      </div>
    </Dialog>

    <!-- Chord Input Dialog -->
    <Dialog v-model:visible="showChordDialog" modal header="Insert Chord" :style="{ width: '20rem' }">
      <div class="flex flex-col gap-4 py-2">
        <InputText v-model="chordInput" placeholder="e.g. Am, G7, Cmaj7" class="w-full" autofocus @keyup.enter="insertChord" />
        <div class="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" text size="small" @click="showChordDialog = false" />
          <Button label="Insert" size="small" @click="insertChord" />
        </div>
      </div>
    </Dialog>
  </div>
</template>
